import { cn } from '@seen/ui'
import { type JSX, type ReactNode, useEffect, useState } from 'react'

/**
 * A spinner that shows after a delay. It feels longer for a user to see a spinner than to see nothing for a few milliseconds, so we delay for a bit until the spinner is shown.
 *
 * @param props - The component props.
 * @param [props.className] - Additional CSS classes to apply to the loader.
 * @returns - The loader component.
 */
export function Loader({ className }: { className?: string }): ReactNode {
  const [didDelay, setDidDelay] = useState(false)
  useEffect(() => {
    const timeoutId = setTimeout(() => setDidDelay(true), 1000)
    return () => clearTimeout(timeoutId)
  }, [])

  if (!didDelay) {
    return null
  }

  return (
    <div
      className={cn('flex size-full items-center justify-center', className)}
    >
      <Spinner />
    </div>
  )
}

interface SpinnerProps {
  className?: string
}

export function Spinner({ className }: SpinnerProps): JSX.Element {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      className={cn('size-8', className)}
    >
      <circle
        cx="12"
        cy="12"
        r="7"
        fill="none"
        stroke="#5DC6B2"
        strokeWidth="0.5"
        opacity="0.2"
      />
      <circle
        cx="12"
        cy="12"
        r="10"
        fill="none"
        stroke="#A7E2C0"
        strokeWidth="0.5"
        opacity="0.2"
      />

      <circle cx="12" cy="12" r="4" fill="#00A7A2" />

      <circle r="1.5" fill="#5DC6B2">
        <animateMotion
          path="M12,12 m-7,0 a7,7 0 1,0 14,0 a7,7 0 1,0 -14,0"
          dur="2s"
          repeatCount="indefinite"
        />
      </circle>

      <circle r="1" fill="#A7E2C0">
        <animateMotion
          path="M12,12 m-10,0 a10,10 0 1,0 20,0 a10,10 0 1,0 -20,0"
          dur="4s"
          repeatCount="indefinite"
        />
      </circle>
    </svg>
  )
}
